import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { useRegions } from '../lib/api';
import type { RegionCardDto, RegionFactKey } from '../types';
import { ConfidenceValue, SourceLink } from './_shared';

// Tab 4 — Regions. The 12 WWD regions, all treated equally. Stats are computed from the
// rigs/wells tables; sourced editorial facts render only when one exists (none invented).

const FACT_LABELS: Record<RegionFactKey, string> = {
  estimated_production: 'Est. production',
  key_fields: 'Key fields',
  outlook_2026: '2026 outlook',
};

export default function Regions() {
  const { data, isLoading, error } = useRegions();

  if (isLoading) return <LoadingSkeleton lines={8} />;
  if (error || !data) return <ErrorState message={(error as Error | null)?.message} />;

  const { regions } = data;

  return (
    <div className="stagger">
      <Card>
        <CardTitle>Regions</CardTitle>
        <CardDescription className="mt-2">
          The twelve offshore markets WWD tracks. Rig and well counts are computed from the
          tracked fleet and wells data — they grow as contractor fixtures land.
        </CardDescription>
      </Card>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {regions.map((r) => (
          <RegionCard key={r.slug} region={r} />
        ))}
      </div>
    </div>
  );
}

function RegionCard({ region }: { region: RegionCardDto }) {
  const chips = [
    { label: 'floaters', value: region.stats.activeFloaters },
    { label: 'jack-ups', value: region.stats.activeJackups },
    { label: 'platforms', value: region.stats.activePlatforms },
    { label: 'high-impact wells', value: region.stats.highImpactWells2026 },
  ];

  return (
    <div className="flex flex-col bg-surface border border-border rounded-(--radius-md) p-5 shadow-[var(--shadow-sm)]">
      <h3 className="text-lg font-semibold text-text">{region.name}</h3>
      <p className="mt-0.5 text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)">
        {region.countries.join(' · ')}
      </p>
      {region.description && <p className="mt-2 text-sm text-muted">{region.description}</p>}

      <div className="mt-3 flex flex-wrap gap-1.5">
        {chips.map((c) => (
          <span
            key={c.label}
            className="inline-flex items-center gap-1 rounded-(--radius-sm) border border-border bg-surface-elevated px-2 py-0.5 text-[0.7rem] text-muted font-(family-name:--font-ui)"
          >
            <span className="font-semibold text-text [font-variant-numeric:tabular-nums]">{c.value}</span>
            {c.label}
          </span>
        ))}
      </div>

      {region.stats.keyOperators.length > 0 && (
        <p className="mt-3 text-sm">
          <span className="text-[0.7rem] uppercase tracking-[0.14em] text-muted font-(family-name:--font-ui)">
            Key operators:&nbsp;
          </span>
          {region.stats.keyOperators.map((o) => o.name).join(', ')}
        </p>
      )}

      {region.facts.length > 0 && (
        <dl className="mt-3 flex flex-col gap-2 border-t border-border pt-3 text-sm">
          {region.facts.map((f) => (
            <div key={f.key}>
              <dt className="text-[0.7rem] uppercase tracking-[0.14em] text-muted font-(family-name:--font-ui)">
                {FACT_LABELS[f.key]}
              </dt>
              <dd className="mt-0.5">
                <ConfidenceValue value={f.value} confidence={f.confidence} estBasis={f.estBasis} />{' '}
                <SourceLink source={f.source} />
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
